import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import {
	Modal,
	Button,
	Layout,
	ReduxControls,
	ProgressIndicator
} from "rhombus-react";
import { reduxForm, Field, change } from "redux-form";

import {
	required,
	inRange,
	isInteger,
	validVideoNameCharacters,
	validVideoNameCharactersLegacyInnovid,
	videoNameMaxLength
} from "widgets/forms/validators/validators";
import { useFlag } from "@innovid/feature-flags-react";
import { FEATURE_FLAGS } from "featureFlag/featureFlag";
import { Assets } from "reducers/Assets";
import { safeDecodeURIComponent } from "utils/common";

import "./TranscodeVideoModal.css";

const TRANSCODE_MODAL_FORM_NAME = "transcode-modal-form";

const ENCODE_VIDEO_TYPE = "job:encode_video";
// const ENCODE_VAST_TYPE = "job:encode_vast_video";

// Values should be an object in the form: { min, max }
/* eslint-disable no-magic-numbers */
export const BOUNDS = {
	// https://flashtalkingus.atlassian.net/wiki/spaces/VPROD/pages/225450107/Video+Encoder+Constraints
	width: { min: 16, max: 9999 }, // pixels
	height: { min: 16, max: 9999 }, // pixels

	videoBitrate: { min: 100, max: 30000 }, // kbps
	audioBitrate: { min: 16, max: 320 }, // kbps

	audioSamplerate: { min: 8000, max: 96000 }, // hz

	framerate: { min: 20, max: 60 },
	audioGain: { min: -60, max: 60 }, // Decibels
};
/* eslint-enable */

/*
	Labels are used to map form field names to their labels, which are also used in error messages.
*/
export const Labels = {
	filename: "File Name",
	width: "Width",
	height: "Height",
	maintainAspect: "Maintain Aspect Ratio",

	videoBitrate: "Video Bitrate",
	audioBitrate: "Audio Bitrate",
	audioSamplerate: "Audio Samplerate",
	audioGain: "Audio Gain",
	framerate: "Framerate",
};

function nameAndLabel(name){
	// We can object spread these to avoid typing error prone duplicate strings in Field props.
	return {
		name,
		label: Labels[name],
	};
}

/*
	These are generated range validator functions, stored as consts.
	Regenerating these would update the Field.validate prop causing an infinite rerender loop.

	e.g. You can't do:
		<Field validate={[inRange(BOUNDS.width)]} />
	But this is okay:
		const widthRange = inRange(BOUNDS.width);
		<Field validate={[widthRange]} />
*/
const widthRange = inRange(BOUNDS.width);
const heightRange = inRange(BOUNDS.height);

const videoBitrateRange = inRange(BOUNDS.videoBitrate);
const audioBitrateRange = inRange(BOUNDS.audioBitrate);
const audioSampleRange = inRange(BOUNDS.audioSamplerate);
const audioGainRange = inRange(BOUNDS.audioGain);
const framerateRange = inRange(BOUNDS.framerate);

const filenameValidators = [required, validVideoNameCharacters, videoNameMaxLength];
const filenameValidatorsLegacyInnovid = [required, validVideoNameCharactersLegacyInnovid, videoNameMaxLength];

export const Normalizers = {
	/*
		We're using normalizers to change another field based on a field changing (to maintain aspect ratio).

		This isn't normally what they're used for, but it's the easiest way to know
		which field changed, and to have access to all values.

		This is all that we're using this for, because there's a bug in normalization (it's possible
		this is remedied in newer versions of redux-form):

			The intended normalization seems to work, but doesn't reflect the changed
			value in the field to the user, if the normalized value isn't different from the old value.

			e.g. If you normalize a field so any value lower than 10 gets set to 10,
			and the field is currently set to 10, if the user types in a 2, it will show 2,
			but the redux store won't be changed (from 10).

		Because of this, we just rely on validation functions and min/max props on number-type Inputs.
	*/
	maintainRatioHeight(dispatch){
		return (height, previousHeight, allValues) => {
			let { maintainAspect, aspectRatio } = allValues;
			if(maintainAspect){
				let newWidth = Math.round(aspectRatio * height);
				dispatch(change(TRANSCODE_MODAL_FORM_NAME, "width", newWidth));
			}
			return height;
		};
	},
	maintainRatioWidth(dispatch){
		return (width, previousWidth, allValues) => {
			let { maintainAspect, aspectRatio } = allValues;
			if(maintainAspect){
				let newHeight = Math.round(width / aspectRatio);
				dispatch(change(TRANSCODE_MODAL_FORM_NAME, "height", newHeight));
			}
			return width;
		};
	},
	restoreRatio(dispatch){
		return (maintainAspect, previous, allValues) => {
			/*
				If someone turns off maintainAspect, then changes dimensions, and then
				turns maintainAspect back on, we just defer to the current width, and
				adjust the height to match the ratio
			*/
			if(maintainAspect){
				let { width, aspectRatio } = allValues;
				let newHeight = Math.round(width / aspectRatio);
				dispatch(change(TRANSCODE_MODAL_FORM_NAME, "height", newHeight));
			}
			return maintainAspect;
		};
	},
};

function ModalContent({ isFormReady }){
	const dispatch = useDispatch();
	const [showAdvanced, setShowAdvanced] = useState(false);
	const supportLegacyInnovidSpecialChars = useFlag(FEATURE_FLAGS.SUPPORT_LEGACY_INNOVID_SPECIAL_CHARS);
	const advancedToggleMessage = showAdvanced ? "Hide Advanced" : "Show Advanced";

	let basicOptions = (
		<div className="basic-options">
			<Field
				{...nameAndLabel("filename")}
				className = "filename-input"
				required
				component = {ReduxControls.Input}
				validate = {supportLegacyInnovidSpecialChars ? filenameValidatorsLegacyInnovid : filenameValidators}
			/>

			<div className="dimensions-row">
				<Field
					{...nameAndLabel("width")}
					className = "number-input"
					required
					component = {ReduxControls.Input}
					type = "number"
					{...BOUNDS.width}
					validate = {[required, widthRange, isInteger]}
					normalize = {Normalizers.maintainRatioWidth(dispatch)}
				/>

				<Field
					{...nameAndLabel("height")}
					className = "number-input"
					required
					type = "number"
					{...BOUNDS.height}
					component = {ReduxControls.Input}
					validate = {[required, heightRange, isInteger]}
					normalize = {Normalizers.maintainRatioHeight(dispatch)}
				/>

				<Field
					{...nameAndLabel("maintainAspect")}
					className = "aspect-input"
					component = {ReduxControls.Checkbox}
					normalize = {Normalizers.restoreRatio(dispatch)}
					useValue
				/>
			</div>
		</div>
	);

	let advancedOptions = (
		<>
			<Field
				{...nameAndLabel("videoBitrate")}
				placeholder = "Auto"
				className = "number-input"
				component = {ReduxControls.Input}
				type = "number"
				{...BOUNDS.videoBitrate}
				validate = {[videoBitrateRange, isInteger]}
			/>

			<Field
				{...nameAndLabel("audioBitrate")}
				placeholder = "Auto"
				className = "number-input"
				component = {ReduxControls.Input}
				type = "number"
				{...BOUNDS.audioBitrate}
				validate = {[audioBitrateRange, isInteger]}
			/>

			<Field
				{...nameAndLabel("audioSamplerate")}
				placeholder = "Auto"
				className = "number-input"
				component = {ReduxControls.Input}
				type = "number"
				{...BOUNDS.audioSamplerate}
				validate = {[audioSampleRange, isInteger]}
			/>

			<Field
				{...nameAndLabel("audioGain")}
				placeholder = "Auto"
				className = "number-input"
				component = {ReduxControls.Input}
				type = "number"
				{...BOUNDS.audioGain}
				validate = {[audioGainRange]}
			/>

			<Field
				{...nameAndLabel("framerate")}
				placeholder = "Auto"
				className = "number-input"
				component = {ReduxControls.Input}
				type = "number"
				{...BOUNDS.framerate}
				validate = {[framerateRange, isInteger]}
			/>
		</>
	);

	return (
		<div className="form-container">
			<ProgressIndicator isLoading={!isFormReady} />
			<div className="advanced-toggle">
				<a onClick={() => { setShowAdvanced(!showAdvanced); }}>
					{advancedToggleMessage}
				</a>
			</div>
			{basicOptions}
			<div className="advanced-options">
				{showAdvanced && advancedOptions}
			</div>
		</div>
	);
}

let ModalForm = reduxForm({
	form: TRANSCODE_MODAL_FORM_NAME,
	enableReinitialize: true,
})(ModalContent);

function parseDimensions(dimensionString){
	/*
		The combined asset video object has a single `dimensions` string
		property that looks like: "1280 x 720"

		We need to pick out the width and height.
	*/
	let matches = dimensionString.split(" x ");
	let [width, height] = matches;

	return {
		width,
		height,
	};
}

function stripExtension(filename){
	if(!filename){
		return null;
	}
	let lastDotIndex = filename.lastIndexOf(".");
	if(lastDotIndex !== -1){
		return filename.substring(0, lastDotIndex);
	}
	return filename;
}

export default function TranscodeVideoModal({ libraryId, videoId, returnPath }){
	const dispatch = useDispatch();

	/*
		Because we route to this modal, and use store-based filter configuration (instead of
		query params, say), there are fair odds that the video asset object we want isn't loaded
		into the store, and won't be anytime soon.

		e.g. If the user refreshes the page, with the modal open, the relevant row might not
		be in the current page of the data grid.

		We don't need the detailed video data to initialize the form fields, but we might
		have to wait for that request to resolve.

		THe form should just show a spinner until we have one or the other.
	*/
	let video = useSelector((state) => {
		return state.assets.items.find(item => item.id === videoId) || {};
	});
	let hasErrors = useSelector(state => state.form[TRANSCODE_MODAL_FORM_NAME]?.syncErrors);
	let formValues = useSelector(state => state.form[TRANSCODE_MODAL_FORM_NAME]?.values);

	// Default `videoDetail` to an empty object so we don't get an error destructuring
	let [videoDetail, setVideoDetail] = useState({});
	let [isTranscoding, setIsTranscoding] = useState(false);

	useEffect(() => {
		/*
			Fetching this acts as a failsafe in case the routed to video isn't in the store.
			We need the video's `videoSource` to make the encode request.
		*/
		dispatch(Assets.fetchAssetDetail(libraryId, "Video", videoId))
			.then((response) => {
				if(response?.success && response?.payload?.data){
					let detail = response.payload.data;
					setVideoDetail(detail);
				}
			});
	}, []);

	// Consolidate the video name and dimensions from potential sources.
	let videoName = video?.name || videoDetail?.name || null;

	let { dimensions } = video;
	let { width, height } = (dimensions && parseDimensions(dimensions)) || { width: videoDetail.bbwidth, height: videoDetail.bbheight };
	let aspectRatio = (width / height);

	if(isNaN(aspectRatio)){
		width = BOUNDS.width.min;
		height = BOUNDS.height.min;
		aspectRatio = width / height;
	}

	let { videoSource } = videoDetail;

	let isFormReady = (videoName !== null); /*
		In order to start using the form, we want to have the video data loaded,
		so we can populate the form with the name and existing width and height.
	*/
	let isTranscodeReady = (videoSource !== undefined); /*
		Before we can actually form a transcode request, we need to have the
		`videoSource` property, which is fetched when the modal is loaded, based
		on the videoId (provided from the route).

		So we use this to keep the submit button disabled until it's ready.
	*/

	function closeModal(){
		dispatch(push(returnPath));
	}

	function handleTranscode(){
		/*
			See Assets.transcodeVideo() or the API docs for how request is structured.
		*/
		if(hasErrors){
			return;
		}

		// Destructure and map values to the keys used in the request.
		/* eslint-disable camelcase */
		let {
			filename,
			audioBitrate: audio_bitrate,
			audioGain: audio_gain,
			audioSamplerate: audio_sample_rate,
			framerate: frame_rate,
			videoBitrate: video_bitrate,
			width,
			height,
		} = formValues;

		// We don't want to add the `customEncodes` value if these values aren't specified.
		let advancedValues = [
			audio_bitrate,
			audio_gain,
			audio_sample_rate,
			frame_rate,
			video_bitrate,
		].filter(value => value !== undefined);

		let hasAdvancedValues = advancedValues.length;

		let customEncodes = [
			{
				audio_bitrate,
				audio_gain,
				audio_sample_rate,
				frame_rate,
				video_bitrate,

				width,
				height,
			}
		];
		/* eslint-enable camelcase */

		let item = {
			advvast: false,
			name: filename,
			type: ENCODE_VIDEO_TYPE,
			overwrite: false,

			width,
			height,
			videoSource,
		};

		if(hasAdvancedValues){
			item.customEncodes = customEncodes;
		}

		let encodeRequest = {
			advvast: false,
			overwrite: false,
			type: ENCODE_VIDEO_TYPE,
			items: [
				item
			],
		};

		setIsTranscoding(true);
		dispatch(Assets.transcodeVideo(libraryId, encodeRequest))
			.then((response) => {
				setIsTranscoding(false);
				closeModal();
			});
	}


	function ModalFooter(){
		let transcodeButtonProps = {
			onClick: handleTranscode,
			disabled: (hasErrors || isTranscoding || !isTranscodeReady),
			isLoading: (!videoSource || isTranscoding),
			icon: isTranscoding ? "spinner" : null,
		};

		return (
			<Layout.ModalFooter>
				<Button
					theme = "default"
					onClick = {closeModal}
				>
					Cancel
				</Button>

				<Button {...transcodeButtonProps}>
					Confirm & Transcode
				</Button>
			</Layout.ModalFooter>
		);
	}

	let bladeProps = {
		verticallyCenter: true,
	};

	let initialValues = {
		filename: stripExtension(safeDecodeURIComponent(videoName)),
		width,
		height,
		maintainAspect: true,
		aspectRatio,
	};

	let formProps = {
		isFormReady,
		initialValues,
	};

	return (
		<Modal
			title = {`Transcode Video - ${safeDecodeURIComponent(videoName)}`}
			type = "modal"
			className = "transcode-video-modal"
			xClose={true}
			allowFullScreen={false}
			bladeProps={bladeProps}
			renderContent = {() => <ModalForm {...formProps} />}
			renderFooter = {ModalFooter}
			onClose = {closeModal}
		/>
	);
}
